import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminApi, formatApiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
    Database, Loader2, RefreshCw, Trash2, Target, Users, Briefcase, Activity, Zap, Shield, ExternalLink, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import BackButton from '../../components/BackButton';
import i18n from 'i18next';

function formatDateTime(iso, lang) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return iso;
    }
}

export default function AdminScoringCachePage() {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [invalidating, setInvalidating] = useState({}); // {scope_id: true}

    useEffect(() => {
        loadStats(true);
    }, []);

    const loadStats = async (initial = false) => {
        if (!initial) setRefreshing(true);
        try {
            const res = await adminApi.getScoringCacheStats();
            setStats(res.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRefreshing(false);
            if (initial) setLoading(false);
        }
    };

    const handleClearAll = async () => {
        setClearing(true);
        try {
            const res = await adminApi.clearScoringCache();
            toast.success(t('scoring_cache.cleared_toast', { count: res.data.deleted_count }));
            await loadStats();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setClearing(false);
        }
    };

    const handleInvalidateCandidate = async (cid, name) => {
        const key = `c:${cid}`;
        setInvalidating((m) => ({ ...m, [key]: true }));
        try {
            const res = await adminApi.invalidateScoringCacheForCandidate(cid);
            const displayName = (name && name !== '—') ? name : `#${cid.slice(0, 8)}`;
            toast.success(t('scoring_cache.invalidated_candidate_toast', { name: displayName, count: res.data.deleted_count }));
            await loadStats();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setInvalidating((m) => { const c = { ...m }; delete c[key]; return c; });
        }
    };

    const handleInvalidateJob = async (jid, title) => {
        const key = `j:${jid}`;
        setInvalidating((m) => ({ ...m, [key]: true }));
        try {
            const res = await adminApi.invalidateScoringCacheForJob(jid);
            const displayTitle = (title && title !== '—') ? title : `#${jid.slice(0, 8)}`;
            toast.success(t('scoring_cache.invalidated_job_toast', { title: displayTitle, count: res.data.deleted_count }));
            await loadStats();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setInvalidating((m) => { const c = { ...m }; delete c[key]; return c; });
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
            </div>
        );
    }

    const kpis = [
        {
            id: 'entries',
            label: t('scoring_cache.kpi_entries'),
            value: stats?.total_entries ?? 0,
            icon: Database,
            accent: 'bg-[#DBEAFE] text-[#002FA7]',
        },
        {
            id: 'hit_rate',
            label: t('scoring_cache.kpi_hit_rate'),
            value: `${(stats?.hit_rate ?? 0).toFixed(1)}%`,
            icon: Target,
            accent: 'bg-[#D1FAE5] text-[#059669]',
        },
        {
            id: 'hits',
            label: t('scoring_cache.kpi_hits'),
            value: stats?.total_hits ?? 0,
            icon: Zap,
            accent: 'bg-[#EDE9FE] text-[#7C3AED]',
        },
        {
            id: 'misses',
            label: t('scoring_cache.kpi_misses'),
            value: stats?.total_misses ?? 0,
            icon: Activity,
            accent: 'bg-[#FEF3C7] text-[#92400E]',
        },
    ];

    return (
        <div className="space-y-6" data-testid="scoring-cache-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <BackButton to="/dashboard/admin" />
                    <h1 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit'] mt-3 flex items-center gap-2">
                        <Database className="w-6 h-6 text-[#002FA7]" />
                        {t('scoring_cache.page_title')}
                    </h1>
                    <p className="text-sm text-[#4B5563] mt-1">{t('scoring_cache.page_subtitle')}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => loadStats()}
                        disabled={refreshing}
                        data-testid="scoring-cache-refresh-btn"
                    >
                        {refreshing ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        {t('common.refresh')}
                    </Button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button
                                type="button"
                                size="sm"
                                disabled={clearing || (stats?.total_entries ?? 0) === 0}
                                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                                data-testid="scoring-cache-clear-btn"
                            >
                                {clearing ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Trash2 className="w-4 h-4 mr-2" />
                                )}
                                {t('scoring_cache.clear_all')}
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>{t('scoring_cache.confirm_clear_title')}</AlertDialogTitle>
                                <AlertDialogDescription>
                                    {t('scoring_cache.confirm_clear_desc', { count: stats?.total_entries ?? 0 })}
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={handleClearAll}
                                    className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                                    data-testid="scoring-cache-confirm-clear"
                                >
                                    {t('scoring_cache.clear_all')}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>

            {/* Rate-limit monitoring banner (super-admin only, lightweight) */}
            {stats?.rate_limit_summary && stats.rate_limit_summary.last_hour_blocks > 0 && (
                <div
                    data-testid="rate-limit-banner"
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg border border-[#FCD34D] bg-[#FFFBEB] text-[#78350F]"
                >
                    <div className="flex items-center gap-2 text-sm">
                        <Shield className="w-4 h-4 flex-shrink-0" />
                        <span data-testid="rate-limit-blocks-count" className="font-medium">
                            {t('scoring_cache.rate_limit_blocks_last_hour', {
                                count: stats.rate_limit_summary.last_hour_blocks,
                            })}
                        </span>
                        {stats.rate_limit_summary.orgs_concerned > 0 && (
                            <span className="text-[#92400E] hidden sm:inline">
                                · {t('scoring_cache.rate_limit_orgs_concerned', {
                                    count: stats.rate_limit_summary.orgs_concerned,
                                })}
                            </span>
                        )}
                    </div>
                    {stats.rate_limit_summary.top_org_today && (
                        <Link
                            to={`/dashboard/admin/audit?org_id=${stats.rate_limit_summary.top_org_today.organization_id}&action=RATE_LIMIT_BLOCKED`}
                            data-testid="rate-limit-top-org"
                            className="text-xs text-[#92400E] truncate flex items-center gap-1 hover:text-[#78350F] hover:underline focus-visible:underline focus-visible:outline-none"
                            title={t('scoring_cache.rate_limit_view_audit_tooltip', { name: stats.rate_limit_summary.top_org_today.name })}
                        >
                            <span className="truncate">
                                {t('scoring_cache.rate_limit_top_org_today', {
                                    name: stats.rate_limit_summary.top_org_today.name,
                                    count: stats.rate_limit_summary.top_org_today.blocks,
                                })}
                            </span>
                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </Link>
                    )}
                </div>
            )}

            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="scoring-cache-kpis">
                {kpis.map((k) => {
                    const Icon = k.icon;
                    return (
                        <div
                            key={k.id}
                            data-testid={`kpi-${k.id}`}
                            className="bg-white p-4 rounded-lg border border-[#E5E7EB]"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <span className={`w-8 h-8 rounded-md flex items-center justify-center ${k.accent}`}>
                                    <Icon className="w-4 h-4" />
                                </span>
                                <span className="text-xs font-medium text-[#4B5563]">{k.label}</span>
                            </div>
                            <p className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">{k.value}</p>
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top candidates */}
                <div className="bg-white rounded-lg border border-[#E5E7EB] p-6" data-testid="top-candidates-section">
                    <div className="flex items-center gap-2 mb-4">
                        <Users className="w-5 h-5 text-[#7C3AED]" />
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                            {t('scoring_cache.top_candidates_title')}
                        </h2>
                    </div>
                    {(stats?.top_candidates?.length ?? 0) === 0 ? (
                        <p className="text-sm text-[#9CA3AF] text-center py-6">{t('scoring_cache.empty_top_candidates')}</p>
                    ) : (
                        <div className="overflow-x-auto -mx-6">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs font-semibold text-[#6B7280] uppercase border-b border-[#E5E7EB]">
                                        <th className="px-6 py-2">{t('scoring_cache.col_name')}</th>
                                        <th className="px-2 py-2 text-right">{t('scoring_cache.col_entries')}</th>
                                        <th className="px-2 py-2 text-right">{t('scoring_cache.col_hits')}</th>
                                        <th className="px-2 py-2 hidden md:table-cell">{t('scoring_cache.col_last_used')}</th>
                                        <th className="px-6 py-2 text-right">{t('scoring_cache.col_actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.top_candidates.map((c) => {
                                        const busy = invalidating[`c:${c.candidate_id}`];
                                        return (
                                            <tr
                                                key={c.candidate_id}
                                                data-testid={`candidate-row-${c.candidate_id}`}
                                                className="border-b border-[#F3F4F6] last:border-0"
                                            >
                                                <td className="px-6 py-2 font-medium text-[#0A0A0A] truncate max-w-[160px]" title={c.name}>{c.name || '—'}</td>
                                                <td className="px-2 py-2 text-right text-[#4B5563]">{c.entries}</td>
                                                <td className="px-2 py-2 text-right font-semibold text-[#059669]">{c.hits}</td>
                                                <td className="px-2 py-2 hidden md:table-cell text-[#6B7280] text-xs">{formatDateTime(c.last_used_at, i18n.language)}</td>
                                                <td className="px-6 py-2 text-right">
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline"
                                                        disabled={busy}
                                                        onClick={() => handleInvalidateCandidate(c.candidate_id, c.name)}
                                                        data-testid={`invalidate-candidate-${c.candidate_id}`}
                                                        title={t('scoring_cache.force_reanalysis_tooltip')}
                                                    >
                                                        {busy ? (
                                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                        ) : (
                                                            <>
                                                                <Trash2 className="w-3.5 h-3.5 mr-1" />
                                                                {t('scoring_cache.force_reanalysis')}
                                                            </>
                                                        )}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Top jobs */}
                <div className="bg-white rounded-lg border border-[#E5E7EB] p-6" data-testid="top-jobs-section">
                    <div className="flex items-center gap-2 mb-4">
                        <Briefcase className="w-5 h-5 text-[#3B82F6]" />
                        <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                            {t('scoring_cache.top_jobs_title')}
                        </h2>
                    </div>
                    {(stats?.top_jobs?.length ?? 0) === 0 ? (
                        <p className="text-sm text-[#9CA3AF] text-center py-6">{t('scoring_cache.empty_top_jobs')}</p>
                    ) : (
                        <div className="overflow-x-auto -mx-6">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs font-semibold text-[#6B7280] uppercase border-b border-[#E5E7EB]">
                                        <th className="px-6 py-2">{t('scoring_cache.col_title')}</th>
                                        <th className="px-2 py-2 text-right">{t('scoring_cache.col_entries')}</th>
                                        <th className="px-2 py-2 text-right">{t('scoring_cache.col_hits')}</th>
                                        <th className="px-2 py-2 hidden md:table-cell">{t('scoring_cache.col_last_used')}</th>
                                        <th className="px-6 py-2 text-right">{t('scoring_cache.col_actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.top_jobs.map((j) => {
                                        const busy = invalidating[`j:${j.job_id}`];
                                        return (
                                            <tr
                                                key={j.job_id}
                                                data-testid={`job-row-${j.job_id}`}
                                                className="border-b border-[#F3F4F6] last:border-0"
                                            >
                                                <td className="px-6 py-2 font-medium text-[#0A0A0A] truncate max-w-[180px]" title={j.title}>{j.title || '—'}</td>
                                                <td className="px-2 py-2 text-right text-[#4B5563]">{j.entries}</td>
                                                <td className="px-2 py-2 text-right font-semibold text-[#059669]">{j.hits}</td>
                                                <td className="px-2 py-2 hidden md:table-cell text-[#6B7280] text-xs">{formatDateTime(j.last_used_at, i18n.language)}</td>
                                                <td className="px-6 py-2 text-right">
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline"
                                                        disabled={busy}
                                                        onClick={() => handleInvalidateJob(j.job_id, j.title)}
                                                        data-testid={`invalidate-job-${j.job_id}`}
                                                        title={t('scoring_cache.force_reanalysis_tooltip')}
                                                    >
                                                        {busy ? (
                                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                        ) : (
                                                            <>
                                                                <Trash2 className="w-3.5 h-3.5 mr-1" />
                                                                {t('scoring_cache.force_reanalysis')}
                                                            </>
                                                        )}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Scoring engine v2 — per-org feature flag */}
            <ScoringEngineSection />
        </div>
    );
}

function ScoringEngineSection() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [toggling, setToggling] = useState({}); // {org_id: true}
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 10;

    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        try {
            const res = await adminApi.getScoringEngineStatus();
            setData(res.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (orgId) => {
        setToggling((m) => ({ ...m, [orgId]: true }));
        try {
            const res = await adminApi.toggleScoringEngine(orgId);
            toast.success(res.data.scoring_engine_v2
                ? t('scoring_engine.toggled_on_toast', { name: res.data.name })
                : t('scoring_engine.toggled_off_toast', { name: res.data.name }));
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setToggling((m) => { const c = { ...m }; delete c[orgId]; return c; });
        }
    };

    const total = data?.orgs?.length ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    // Clamp page if data shrinks (e.g. after refresh)
    const currentPage = Math.min(page, totalPages - 1);
    const paginatedOrgs = (data?.orgs ?? []).slice(
        currentPage * PAGE_SIZE,
        currentPage * PAGE_SIZE + PAGE_SIZE
    );

    return (
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-6" data-testid="scoring-engine-section">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#7C3AED]" />
                    <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                        {t('scoring_engine.title')}
                    </h2>
                </div>
                {data && (
                    <span className="text-xs text-[#6B7280]" data-testid="scoring-engine-counter">
                        {t('scoring_engine.enabled_counter', { count: data.v2_enabled_count, total: data.total_orgs })}
                    </span>
                )}
            </div>
            <p className="text-sm text-[#4B5563] mb-4">{t('scoring_engine.subtitle')}</p>

            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#7C3AED]" /></div>
            ) : !data || data.orgs.length === 0 ? (
                <p className="text-sm text-[#9CA3AF] text-center py-6">{t('scoring_engine.empty')}</p>
            ) : (
                <>
                <div className="overflow-x-auto -mx-6">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs font-semibold text-[#6B7280] uppercase border-b border-[#E5E7EB]">
                                <th className="px-6 py-2">{t('scoring_engine.col_org')}</th>
                                <th className="px-2 py-2 hidden sm:table-cell">{t('scoring_engine.col_plan')}</th>
                                <th className="px-2 py-2 text-center">{t('scoring_engine.col_engine')}</th>
                                <th className="px-6 py-2 text-right">{t('scoring_engine.col_action')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedOrgs.map((o) => {
                                const busy = toggling[o.id];
                                return (
                                    <tr
                                        key={o.id}
                                        data-testid={`engine-row-${o.id}`}
                                        className="border-b border-[#F3F4F6] last:border-0"
                                    >
                                        <td className="px-6 py-2 font-medium text-[#0A0A0A] truncate max-w-[200px]" title={o.name}>{o.name}</td>
                                        <td className="px-2 py-2 hidden sm:table-cell text-[#4B5563] text-xs capitalize">{o.subscription_plan}</td>
                                        <td className="px-2 py-2 text-center">
                                            {o.scoring_engine_v2 ? (
                                                <span data-testid={`engine-badge-${o.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EDE9FE] text-[#6D28D9] text-xs font-semibold">
                                                    <Sparkles className="w-3 h-3" /> v2
                                                </span>
                                            ) : (
                                                <span data-testid={`engine-badge-${o.id}`} className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280] text-xs font-semibold">
                                                    v1
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-2 text-right">
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant={o.scoring_engine_v2 ? 'outline' : 'default'}
                                                disabled={busy}
                                                onClick={() => handleToggle(o.id)}
                                                data-testid={`engine-toggle-${o.id}`}
                                                className={o.scoring_engine_v2 ? '' : 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white'}
                                            >
                                                {busy ? (
                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                ) : o.scoring_engine_v2 ? (
                                                    t('scoring_engine.disable')
                                                ) : (
                                                    t('scoring_engine.enable')
                                                )}
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-4 mt-2 border-t border-[#E5E7EB]"
                    data-testid="scoring-engine-pagination"
                >
                    <p className="text-xs text-[#6B7280]">
                        {total === 0
                            ? t('scoring_engine.pagination_none')
                            : t('scoring_engine.pagination_range', {
                                from: currentPage * PAGE_SIZE + 1,
                                to: Math.min((currentPage + 1) * PAGE_SIZE, total),
                                total,
                            })}
                    </p>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={currentPage === 0}
                            data-testid="scoring-engine-prev"
                        >
                            {t('common.previous')}
                        </Button>
                        <span className="text-xs text-[#4B5563] px-2" data-testid="scoring-engine-page-indicator">
                            {t('scoring_engine.page_n_of', { current: currentPage + 1, total: totalPages })}
                        </span>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                            disabled={currentPage >= totalPages - 1}
                            data-testid="scoring-engine-next"
                        >
                            {t('common.next')}
                        </Button>
                    </div>
                </div>
                </>
            )}
        </div>
    );
}
